import React, { useState, useEffect, useMemo } from "react";
import { Controller, useForm, FormProvider } from "react-hook-form";
import Select, { components } from "react-select";
import makeAnimated from "react-select/animated";
import Datepicker from "react-tailwindcss-datepicker";
import {
  UseGetRfiListForFilters,
  usePostAssessmentsRequests,
  useListOfQuestionnaireTemplate,
  useCreatePurchaseVendors,
  useListOfPurchaseVendors,
  useSendRfiToPurchaseVendor,
  useAddProviderLocation,
  useAddProviderContact,
} from "../custom-hooks/queries";
import ComboBox from "../components/ComboBox";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import ProviderContactDrawer from "../components/ProviderContactDrawer";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  newVendorRequestSchema,
  withSubmitFlag,
} from "../Schema/newVendorRequestSchema";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import NotificationModal from "../components/NotificationModal";
import { useDispatch, useSelector } from "react-redux";
import { setDrawer } from "../redux-store/features/layoutSlice";
import DatePickerController from "../components/datePickerController";
import {
  dateFormatRFi,
  fortyFiveDays,
  userHasRight,
} from "../utils/helperMethods";
import LoadingModal from "../components/Skeletons/LoadingModal";
import { getGoogleMapsApiKey } from "../config/googleMapsConfig";
import {
  countryOptions,
  getCountryByValue,
  getFilteredCountryOptions,
  getFilteredStateOptions,
  getStateOptions,
  parseAddressComponents,
} from "../utils/locationData";
import HttpsUrlInput from "../components/HttpsUrlInput";
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {
  getProviderOrgDetails,
  getPurchaseVendorBySponsor,
} from "../api/api-methods";
import { newVendorRightsList } from "../utils/constants";
import { onBlurSelectWithDelay } from "../utils/dom-events";
import LocationFields from "../components/LocationFields";
import ContactFields from "../components/ContactFields";
import { getLocationFromProvider } from "../utils/addressUtils";
import { resolveVqqLocationPart } from "../utils/vqqNameFormat";
import { validateAdditionalLocations } from "../utils/locationFieldsValidation";
import CustomTooltip from "../components/Filters/CustomTooltip";
import { toCapabilitiesPayload } from "../utils/foundationScope";
import { isDuringMaintenance } from "../utils/maintenance";
import { createAndDesignateNewVendorContacts } from "../utils/newVendorContactDesignations";
import {
  useCanRequestAutoScore,
  useCanRequestSmeScore,
} from "../utils/orgFeatureGates";
import { resolveRiskAddOnRequest } from "../utils/vqqRiskAddOns";
import { resolveContactPersonName } from "../utils/requestContactFormUtils";

const animatedComponents = makeAnimated();
const libraries = ["places"]; // We need 'places' for Google Places Autocomplete

export const NewRequestVendor = () => {
  const [showRfiSection, setShowRfiSection] = useState(false);
  const [showAssessmentSection, setShowAssessmentSection] = useState(false);
  const { data: providers } = useListOfPurchaseVendors();

  // 14028 — Risk Scoring add-on selection mirrors the standalone Request New VQQ
  // form. Each checkbox is visible only when the sponsor org is entitled AND has
  // remaining quota (Unlimited counts as remaining); if both are hidden, the
  // entire Add-Ons group is hidden. Flags travel with the VQQ via sendRfis.
  const canRequestAutoScore = useCanRequestAutoScore();
  const canRequestSmeScore = useCanRequestSmeScore();
  const [enableAutoScore, setEnableAutoScore] = useState(false);
  const [enableSmeScore, setEnableSmeScore] = useState(false);

  const methods = useForm({
    resolver: yupResolver(newVendorRequestSchema),
    mode: "onTouched",
    defaultValues: {
      locationType: "primary",
      onsiteRemote: { onsite: false, remote: true },
      serviceCategories: [],
    },
    context: {
      showRfiSection,
      showAssessmentSection,
    },
  });
  const newGuid = uuidv4();

  const {
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    reset,
    register,
    control,
  } = methods;
  // Risk scoring add-ons exist only for Diligent templates: a custom template
  // hides the whole block and sends both flags off.
  const riskAddOns = resolveRiskAddOnRequest({ templates: watch("questionnaireTemplates") || [], canRequestAutoScore, canRequestSmeScore, enableAutoScore, enableSmeScore });
  const { data: rfis } = UseGetRfiListForFilters();
  const { data: questionnaires } = useListOfQuestionnaireTemplate();
  const { rights } = useSelector((state) => state.userRights);

  const serviceCategoryOptions = useMemo(() => {
    if (!rfis?.length) return [];
    const seenIds = new Set();
    const seenLabels = new Set();
    const out = [];
    for (const item of rfis) {
      const id = item?.rfi_id ?? item?.Rfi_Id;
      const label = String(item?.rfi_name ?? item?.name ?? "").trim();
      if (id == null || !label) continue;
      const idKey = String(id);
      const labelKey = label.toLowerCase();
      if (seenIds.has(idKey)) continue;
      if (seenLabels.has(labelKey)) continue;
      seenIds.add(idKey);
      seenLabels.add(labelKey);
      out.push({ value: id, label });
    }
    return out;
  }, [rfis]);
  const filtered = questionnaires?.filter((item) => item?.name !== "Core RFI");

  const questionnaireOptions = filtered?.map((item) => ({
    value: item?.hash_id,
    label: item?.name,
    global: item?.global,
  }));
  const [show, setShow] = useState(false);
  /** When true, the modal uses error styling (validation, permissions, or catch-path errors). */
  const [notifyAsError, setNotifyAsError] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const {
    mutateAsync: mutateVqa,
    error: vqaError,
    isError: isVqaError,
  } = usePostAssessmentsRequests();

  const {
    mutateAsync: mutateCreateNewVendor,
    error: rfiError,
    isError: isRfiError,
    data: vendorResponseData,
  } = useCreatePurchaseVendors();
  const { mutateAsync: sendRfis } = useSendRfiToPurchaseVendor();
  const { mutateAsync: addProviderLocation } = useAddProviderLocation();
  const { mutateAsync: addProviderContact } = useAddProviderContact();

  const today = new Date();
  const twoWeeks = new Date();
  twoWeeks.setDate(today.getDate() + 14); // Add 14 days to today

  const [autocomplete, setAutocomplete] = useState(null);
  const [additionalLocationAutocompletes, setAdditionalLocationAutocompletes] =
    useState({});
  const dispatch = useDispatch();

  const [primaryAddress, setPrimaryAddress] = useState("");
  const [primaryCity, setPrimaryCity] = useState("");
  const [primaryCountry, setPrimaryCountry] = useState("");
  const [primaryState, setPrimaryState] = useState("");
  const [additionalLocations, setAdditionalLocations] = useState([]);

  // Dropdown state management (primary location)
  const [isPrimaryCountryDropdownOpen, setIsPrimaryCountryDropdownOpen] =
    useState(false);
  const [isPrimaryStateDropdownOpen, setIsPrimaryStateDropdownOpen] =
    useState(false);

  // Search state for filtering
  const [primaryCountrySearch, setPrimaryCountrySearch] = useState("");
  const [primaryStateSearch, setPrimaryStateSearch] = useState("");
  const [isRFILocationDropdownOpen, setIsRFILocationDropdownOpen] =
    useState(false);
  const [
    isAssessmentLocationDropdownOpen,
    setIsAssessmentLocationDropdownOpen,
  ] = useState(false);
  const [isRFIContactDropdownOpen, setIsRFIContactDropdownOpen] =
    useState(false);
  const [currentSelectedRFIContact, currentSetSelectedRFIContact] =
    useState(null);
  const [isAssessmentContactDropdownOpen, setIsAssessmentContactDropdownOpen] =
    useState(false);

  const [currentSelectedRFILocation, currentSetSelectedRFILocation] =
    useState(null);
  const [
    currentSelectedAssessmentLocation,
    currentSetSelectedAssessmentLocation,
  ] = useState(null);
  const [
    currentSelectedAssessmentContact,
    currentSetSelectedAssessmentContact,
  ] = useState(null);

  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");
  const [contactTitle, setContactTitle] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const apiKey = getGoogleMapsApiKey();
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries: libraries,
  });

  const handleOnLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };
  const locationType = watch("locationType"); // Watch the locationType field to determine which is selected
  const [contactOpen, setContactOpen] = useState(false);

  const [formData, setFormData] = useState({
    mainContactName: "",
    mainContactEmail: "",
  });

  // Store additional contacts added via drawer
  const [additionalContacts, setAdditionalContacts] = useState([]);

  // Handler to add new contact from drawer
  const handleAddContact = (contact) => {
    setAdditionalContacts((prev) => [...prev, contact]);
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#AB54DB" : "white", // Purple background on hover
      color: state.isFocused ? "white" : "black", // White text on hover for better contrast
    }),
  };

  const [formLoading, setFormLoading] = useState(false);
  const { hcUserId } = useSelector((state) => state.userRights);

  const getMainContactName = () =>
    `${contactFirstName || ""} ${contactLastName || ""}`.trim();

  const onSubmit = withSubmitFlag(async (data) => {
    const transformedCapabilities = toCapabilitiesPayload(
      data?.serviceCategories,
      { lockFoundation: showAssessmentSection }
    );

    const canRequestRfi =
      userHasRight(rights, "Request RFIs") && !isDuringMaintenance();
    const canRequestAudit = userHasRight(rights, "Request Audit Reports");

    if (!canRequestRfi && !canRequestAudit) {
      setNotifyAsError(true);
      setMessage(
        'You do not have permission to submit vendor requests. Ask an administrator to grant "Request RFIs" and/or "Request Audit Reports".',
      );
      setShow(true);
      return;
    }

    // If neither section was selected, show a message (no API call)
    if (!showRfiSection && !showAssessmentSection) {
      setNotifyAsError(true);
      setMessage(
        "Please select at least one request type (VQQ or Audit request).",
      );
      setShow(true);
      return;
    }

    // Common vendor data for purchase vendor creation
    const newVendorData = {
      assessment_cadence: null,
      provider_address1: data?.primaryLocationTextBox || "",
      other_address: data?.secondaryLocationTextBox || "",
      business_criticality: null,
      contact_email: contactEmail?.toLowerCase() || "",
      data_access: ["test"],
      description: null,
      name: data?.providerName || "",
      notes: null,
      url: data?.providerUrl,
      purchaser_vendor_contacts: [
        {
          contact_email: contactEmail?.toLowerCase() || "",
          is_primary: true,
          name: getMainContactName() || "",
        },
      ],
      risk_level: null,
      status: null,
      vendor_added_date: null,
      provider_capabilities: showAssessmentSection
        ? transformedCapabilities
        : null,
    };

    // Track if any API call was successful
    let apiSuccess = false;
    let vendorResponse = null;

    setFormLoading(true);

    try {
      // Create purchase vendor only once
      vendorResponse = await mutateCreateNewVendor({ data: newVendorData });
      const providerId =
        vendorResponse?.provider_id ??
        vendorResponse?.providerId ??
        newGuid;
      const vendorOrgData = await getProviderOrgDetails(providerId);
      const organization_id = vendorOrgData?.hc_org_id || "";
      const providerDataRoles = {
        provider_user_subrole: [
          {
            Subrole: "Vendor Admin",
          },
        ],
        provider_user_permissions: newVendorRightsList.map((permission) => ({
          permission: permission,
        })),
      };

      await createAndDesignateNewVendorContacts({
        providerId,
        organization_id,
        providerName: data?.providerName || "",
        providerDataRoles,
        preferredContact: {
          email: contactEmail,
          first_name: contactFirstName,
          last_name: contactLastName,
          job_title: contactTitle,
        },
        additionalContacts,
        showRfiSection,
        showAssessmentSection,
        addProviderContact,
      });

      const vendorLocations = [
        {
          address1: data?.primaryAddress || "",
          city: data?.primaryCity || "",
          country: data?.primaryCountry || "",
          state: data?.primaryState || "",
          provider_id: providerId,
          location_type: "primary",
        },
        ...additionalLocations?.map((location) => ({
          address1: location?.address || "",
          city: location?.city || "",
          country: location?.country || "",
          state: location?.state || "",
          provider_id: providerId,
          location_type: "additional",
        })),
      ];
      // send locations to endpoint (use the new endpoint to send the list)
      if (vendorLocations && vendorLocations.length > 0) {
        await Promise.all(
          vendorLocations.map((location) =>
            addProviderLocation({ provider_id: providerId, location }),
          ),
        );
      }

      // Common vendor information that will be used by both requests
      // Now we can use the provider_id from the successful vendor creation response
      const commonVendorInfo = {
        providerId: providerId,
        providerName: data?.providerName || "",
        providerMainContactName: getMainContactName() || "",
        providerMainContactEmail: data?.contactEmail.toLowerCase() || "",
        providerUrl: data?.providerUrl || "",
        // location
        providerAddress1: data?.primaryAddress || "",
        providerCity: data?.primaryCity || "",
        providerCountry: data?.primaryCountry || "",
        providerState: data?.primaryState || "",
        providerZip: data?.zipCode || "",
        providerPhoneNumber: data?.providerPhoneNumber || "",
        is_provider_new: true,
      };

      // Handle RFI requests if RFI section is selected
      if (showRfiSection) {
        let uniqueName = "";
        const purchaseVendor = await getPurchaseVendorBySponsor(providerId);

        // Send RFIs using the vendor response
        for (let services of data?.questionnaireTemplates) {
          // The locator has to be the same "City, State" the app shows on the VQQ, otherwise
          // HyperComply and D360 disagree on the name.
          const locationPart =
            resolveVqqLocationPart(currentSelectedRFILocation) ||
            getLocationFromProvider(currentSelectedRFILocation, { short: true, abbvState: true });
          uniqueName = `Diligent - ${services?.label} - ${dateFormatRFi(
            data.expectedDate?.startDate,
          ).toUpperCase()} - ${locationPart}`;

          const rfiRequest = {
            name: uniqueName,
            template_id: services?.value,
            due_by: data?.expectedDate?.startDate,
            invite_message: data.rfiAdditionalDetails || "",
            send_questionnaire: true,
            vendor_name: vendorResponse?.name,
            contact_name:
              resolveContactPersonName(currentSelectedRFIContact) ||
              getMainContactName() ||
              "",
            template_name: services?.label || "",
            emails: [currentSelectedRFIContact?.email.toLowerCase() || ""],
            is_global_template: services?.global || false,
            owner_id: hcUserId,
            provider_id: vendorResponse?.provider_id,
            location: currentSelectedRFILocation?.address || "",
            location_city: currentSelectedRFILocation?.city || "",
            location_state: currentSelectedRFILocation?.state || "",
            location_country: currentSelectedRFILocation?.country || "",
            // 14028 — only attach when the sponsor org has the entitlement AND the user opted in.
            ...riskAddOns.forTemplate(services),
          };

          await sendRfis({
            id: purchaseVendor?.hc_purchase_vendor_id,
            data: rfiRequest,
          });
        }

        apiSuccess = true;
      }

      // Handle VQA assessment if Assessment section is selected
      if (showAssessmentSection) {
        // VQA assessment data
        const vqaData = {
          ...commonVendorInfo,
          providerType: "",

          // assessmentContact: currentSelectedAssessmentContact || [],
          email: currentSelectedAssessmentContact?.email,
          firstName: currentSelectedAssessmentContact?.firstName,
          lastName: currentSelectedAssessmentContact?.lastName,
          jobTitle: currentSelectedAssessmentContact?.title,
          // assessmentLocation: currentSelectedAssessmentLocation || [],
          providerAddress1: currentSelectedAssessmentLocation?.address || "",
          providerCity: currentSelectedAssessmentLocation?.city || "",
          providerState: currentSelectedAssessmentLocation?.state || "",
          providerCountry: currentSelectedAssessmentLocation?.country || "",

          capabilities: transformedCapabilities,
          desiredInitialDraftDate: data?.draftDate?.startDate || "",
          additionalDetails: data?.additionalDetails || "",
          isMultiSponsorAssessment: true,
          onsite: data?.onsiteRemote?.onsite || false,
          remote: data?.onsiteRemote?.remote || false,
          vqaType: data?.onsiteRemote?.onsite === true ? "onsite" : "remote",
        };

        await mutateVqa(vqaData);
        apiSuccess = true;
      }

      // Show success message
      if (apiSuccess) {
        const successMessage =
          showRfiSection && showAssessmentSection
            ? "Vendor requests submitted successfully!"
            : showRfiSection
              ? "VQQ request submitted successfully!"
              : "VQA assessment request submitted successfully!";

        handleApiSuccess(successMessage);
        setFormLoading(false);
        // No automatic redirect: success modal stays until user clicks "View Vendors" or "Close"
      }
    } catch (error) {
      setFormLoading(false);
      handleApiError("Request failed", error);
    }

    // Helper function to handle successful API responses
    function handleApiSuccess(successMessage) {
      setNotifyAsError(false);
      setMessage(successMessage);
      setShow(true);
      dispatch(setDrawer(true));
      // Form is reset only when user clicks Close (handleSuccessModalClose)
    }

    // Helper function to handle API errors
    function handleApiError(errorPrefix, error) {
      setNotifyAsError(true);
      setShow(true);
      setMessage(
        `${errorPrefix}: ${
          error?.response?.data?.message || "Something went wrong!"
        }`,
      );
    }
  });
  // Update context whenever the section visibility changes

  useEffect(() => {
    // This ensures the validation context is updated when sections are toggled
    methods.formState.context = {
      showRfiSection,
      showAssessmentSection,
    };
  }, [showRfiSection, showAssessmentSection]); // Update RFI location options when primary or secondary address changes

  useEffect(() => {
    // Use the watch function to subscribe to form field changes
    const subscription = watch((value, { name }) => {
      // Only proceed if the change is in one of the address fields and RFI section is shown
      if (
        (name === "primaryLocationTextBox" ||
          name === "secondaryLocationTextBox") &&
        showRfiSection
      ) {
        // We don't need to do anything here - the options will be updated dynamically
        // in the Select component's options prop, and the user can choose from them
      }
    }); // We don't need to do any initial setup - the options will be shown in the dropdown
    // but won't be selected automatically. The user will need to explicitly select them.

    // Clean up subscription
    return () => subscription.unsubscribe();
  }, [showRfiSection, setValue, watch]);

  const availableLocations = useMemo(
    () =>
      [
        {
          address: primaryAddress,
          city: primaryCity,
          state: primaryState,
          country: primaryCountry,
        },
        ...additionalLocations,
      ]
        .map((loc, i) => ({
          ...loc,
          id: Date.now() + i,
          label: i === 0 ? "Primary Location" : `Additional Location ${i}`,
          fullAddress: getLocationFromProvider(loc, { short: false, abbvState: true }),
        }))
        .filter((loc) => loc.address),
    [
      primaryAddress,
      primaryCity,
      primaryState,
      primaryCountry,
      additionalLocations,
    ],
  );

  const availableContacts = useMemo(
    () =>
      [
        {
          firstName: contactFirstName,
          lastName: contactLastName,
          title: contactTitle,
          email: contactEmail,
        },
        ...additionalContacts,
      ]
        .map((loc, i) => ({
          ...loc,
          id: Date.now() + i,
          label: i === 0 ? "Preferred Contact" : `Additional Contact ${i}`,
          fullContact: [
            [loc.firstName, loc.lastName].join(" "),
            loc.title,
            // loc.email,
          ]
            .filter((item) => item)
            .join(", "),
        }))
        .filter((loc) => loc.firstName),
    [
      contactFirstName,
      contactLastName,
      contactTitle,
      contactEmail,
      additionalContacts,
    ],
  );

  // Update selected location when available locations change
  useEffect(() => {
    if (availableLocations.length > 0 && !currentSelectedRFILocation) {
      currentSetSelectedRFILocation(availableLocations[0]);
    }
    if (
      currentSelectedRFILocation &&
      !availableLocations.find(
        (loc) => loc.id === currentSelectedRFILocation.id,
      )
    ) {
      currentSetSelectedRFILocation(
        availableLocations.length > 0 ? availableLocations[0] : null,
      );
    }
  }, [
    availableLocations,
    currentSelectedRFILocation,
    currentSetSelectedRFILocation,
  ]);

  // Update selected location when available locations change
  useEffect(() => {
    if (availableLocations.length > 0 && !currentSelectedAssessmentLocation) {
      currentSetSelectedAssessmentLocation(availableLocations[0]);
    }
    if (
      currentSelectedAssessmentLocation &&
      !availableLocations.find(
        (loc) => loc.id === currentSelectedAssessmentLocation.id,
      )
    ) {
      currentSetSelectedAssessmentLocation(
        availableLocations.length > 0 ? availableLocations[0] : null,
      );
    }
  }, [
    availableLocations,
    currentSelectedAssessmentLocation,
    currentSetSelectedAssessmentLocation,
  ]);

  useEffect(() => {
    if (availableContacts.length > 0 && !currentSelectedRFIContact) {
      currentSetSelectedRFIContact(availableContacts[0]);
    }
    if (
      currentSelectedRFIContact &&
      !availableContacts.find((loc) => loc.id === currentSelectedRFIContact.id)
    ) {
      currentSetSelectedRFIContact(
        availableContacts.length > 0 ? availableContacts[0] : null,
      );
    }
  }, [
    availableContacts,
    currentSelectedRFIContact,
    currentSetSelectedRFIContact,
  ]);

  useEffect(() => {
    if (availableContacts.length > 0 && !currentSelectedAssessmentContact) {
      currentSetSelectedAssessmentContact(availableContacts[0]);
    }
    if (
      currentSelectedAssessmentContact &&
      !availableContacts.find(
        (loc) => loc.id === currentSelectedAssessmentContact.id,
      )
    ) {
      currentSetSelectedAssessmentContact(
        availableContacts.length > 0 ? availableContacts[0] : null,
      );
    }
  }, [
    availableContacts,
    currentSelectedAssessmentContact,
    currentSetSelectedAssessmentContact,
  ]);

  const addAdditionalContact = () => {
    setAdditionalContacts((prev) => [
      ...prev,
      {
        firstName: "",
        lastName: "",
        title: "",
        email: "",
      },
    ]);
  };

  const updateAdditionalContact = (index, field, value) => {
    setAdditionalContacts((prev) =>
      prev.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact,
      ),
    );
  };

  const removeAdditionalContact = (index) => {
    setAdditionalContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();

      const { streetAddress, city, state, countryCode } =
        parseAddressComponents(place);

      setPrimaryAddress(streetAddress);
      setPrimaryCity(city);
      setPrimaryState(state);
      setPrimaryCountry(countryCode);

      setValue("primaryAddress", streetAddress);
      setValue("primaryCity", city);
      setValue("primaryState", state);
      setValue("primaryCountry", countryCode);
      setIsPrimaryCountryDropdownOpen(false);
      setIsPrimaryStateDropdownOpen(false);
    }
  };

  // Handle additional location autocomplete loading
  const handleAdditionalLocationOnLoad = (autocompleteInstance, index) => {
    setAdditionalLocationAutocompletes((prev) => ({
      ...prev,
      [index]: autocompleteInstance,
    }));
  };

  // Handle additional location place selection
  const handleAdditionalLocationPlaceChanged = (index) => {
    const autocompleteInstance = additionalLocationAutocompletes[index];
    if (autocompleteInstance) {
      const place = autocompleteInstance.getPlace();
      const { streetAddress, city, state, countryCode } =
        parseAddressComponents(place);

      updateAdditionalLocation(index, "address", streetAddress);
      updateAdditionalLocation(index, "city", city);
      updateAdditionalLocation(index, "state", state);
      updateAdditionalLocation(index, "country", countryCode);
    }
  };

  const addAdditionalLocation = () => {
    setAdditionalLocations((prev) => [
      ...prev,
      {
        address: "",
        city: "",
        state: "",
        country: "",
      },
    ]);
  };

  const updateAdditionalLocation = (index, field, value) => {
    setAdditionalLocations((prev) =>
      prev.map((location, i) =>
        i === index ? { ...location, [field]: value } : location,
      ),
    );
    const formKey =
      field === "address"
        ? "Address"
        : field === "city"
          ? "City"
          : field === "state"
            ? "State"
            : field === "country"
              ? "Country"
              : null;
    if (formKey) {
      setValue(`additional_${index}_${formKey}`, value ?? "");
    }
  };

  const removeAdditionalLocation = (index) => {
    setAdditionalLocations((prev) => prev.filter((_, i) => i !== index));
    clearErrors(`additional_${index}_Address`);
    clearErrors(`additional_${index}_City`);
    clearErrors(`additional_${index}_State`);
    clearErrors(`additional_${index}_Country`);
    // Clean up autocomplete state for removed location
    setAdditionalLocationAutocompletes((prev) => {
      const newState = { ...prev };
      delete newState[index];
      // Re-index remaining autocompletes
      const reindexed = {};
      Object.keys(newState).forEach((key) => {
        const keyIndex = parseInt(key);
        if (keyIndex > index) {
          reindexed[keyIndex - 1] = newState[key];
        } else {
          reindexed[key] = newState[key];
        }
      });
      return reindexed;
    });
  };

  const submitWithLocationValidation = (onValid) =>
    handleSubmit(
      (data) => {
        if (
          !validateAdditionalLocations(additionalLocations, {
            setError,
            clearErrors,
          })
        ) {
          return;
        }
        return onValid(data);
      },
      () => {
        validateAdditionalLocations(additionalLocations, {
          setError,
          clearErrors,
        });
      },
    );

  // Determine notification title based on which requests were selected
  const getNotificationTitle = () => {
    if (showRfiSection && showAssessmentSection) {
      return "Vendor requests submitted";
    }
    if (showRfiSection) {
      return "VQQ request submitted";
    }
    if (showAssessmentSection) {
      return "VQA assessment request submitted";
    }
    return "Vendor request successful";
  };

  const isMutationError = isVqaError || isRfiError;
  const modalShowsError = isMutationError || notifyAsError;

  const handleNotificationModalClose = () => {
    if (!modalShowsError) {
      handleSuccessModalClose();
    }
  };

  useEffect(() => {
    if (!show) {
      // Let DialogPanel finish its leave transition before clearing error/success
      // presentation flags; otherwise the panel can briefly render as "success"
      // while still visible (notifyAsError flips false while open animates to closed).
      const t = window.setTimeout(() => {
        setNotifyAsError(false);
        setMessage("");
      }, 280);
      return () => window.clearTimeout(t);
    }
  }, [show]);

  const handleSuccessModalClose = () => {
    reset();
    setPrimaryAddress("");
    setPrimaryCity("");
    setPrimaryState("");
    setPrimaryCountry("");
    setAdditionalLocations([]);
    setContactFirstName("");
    setContactLastName("");
    setContactTitle("");
    setContactEmail("");
    setFormData({ mainContactName: "", mainContactEmail: "" });
    setAdditionalContacts([]);
    setShowRfiSection(false);
    setShowAssessmentSection(false);
  };

  return (
    <div className="px-5">
      <FormProvider {...methods}>
        <form onSubmit={submitWithLocationValidation(onSubmit)}>
          <fieldset
            disabled={formLoading}
            className={
              formLoading
                ? "opacity-50 cursor-not-allowed pointer-events-none"
                : ""
            }
            aria-busy={formLoading}
          >
            {/* General Vendor Information */}
            <div className="mt-3.5 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
              <h1 className="font-semibold">Create New Vendor with Requests</h1>
              <p className="text-sm text-gray-600">
                Complete the information below to request a new vendor to be
                added to the Diligent Qualification Platform.
              </p>
            </div>
            <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 border-b border-gray-300 py-5 lg:py-6">
              <h1 className="block text-sm font-medium text-gray-900 mt-4 md:mt-0">
                Vendor Name*
              </h1>
              <div className="flex flex-col space-y-3">
                <span className="space-x-3">
                  <input
                    type="text"
                    {...register("providerName")}
                    className="ring-0 border-gray-300 rounded-md text-sm  w-[300px] lg:w-[400px] xl:w-[500px] "
                    placeholder="Enter Vendor Name"
                  />
                </span>
                {errors.providerName && (
                  <p className="text-red-500 text-sm">
                    {errors.providerName.message}
                  </p>
                )}
              </div>
            </div>
            <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0 border-b border-gray-300 py-5 lg:py-6">
              <h1 className="block text-sm font-medium text-gray-900">
                Vendor URL
              </h1>
              <div className="flex flex-col space-y-3 w-[300px] lg:w-[400px] xl:w-[500px]">
                <HttpsUrlInput
                  name="providerUrl"
                  label=""
                  placeholder="www.vendorwebsite.com"
                />
              </div>
            </div>
            <div className="flex flex-col border-b border-gray-300 py-5 lg:py-6">
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 py-5 lg:py-6">
                <div className="flex items-center justify-between">
                  <h1 className="block text-sm font-medium text-gray-900 md:mt-0">
                    Primary Location
                  </h1>
                </div>
                <LocationFields
                  control={control}
                  errors={errors}
                  isLoaded={isLoaded}
                  handleOnLoad={handleOnLoad}
                  handlePlaceChanged={handlePlaceChanged}
                  address={primaryAddress}
                  setAddress={setPrimaryAddress}
                  setValue={setValue}
                  onChangeLocation={() => {}}
                  city={primaryCity}
                  setCity={setPrimaryCity}
                  state={primaryState}
                  setState={setPrimaryState}
                  country={primaryCountry}
                  setCountry={setPrimaryCountry}
                  countrySearch={primaryCountrySearch}
                  setCountrySearch={setPrimaryCountrySearch}
                  isCountryDropdownOpen={isPrimaryCountryDropdownOpen}
                  setIsCountryDropdownOpen={setIsPrimaryCountryDropdownOpen}
                  isStateDropdownOpen={isPrimaryStateDropdownOpen}
                  setIsStateDropdownOpen={setIsPrimaryStateDropdownOpen}
                  stateSearch={primaryStateSearch}
                  setStateSearch={setPrimaryStateSearch}
                  getFilteredCountryOptions={getFilteredCountryOptions}
                  countryOptions={countryOptions}
                  getStateOptions={getStateOptions}
                  getFilteredStateOptions={getFilteredStateOptions}
                />
              </div>

              {/* Display Additional Locations - reusing LocationFields per item */}
              {additionalLocations.map((location, index) => {
                const locationLabel =
                  index === 0
                    ? "Additional Location"
                    : `Additional Location ${index + 1}`;
                return (
                  <div
                    key={index}
                    className={`sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 py-3 ${index === 0 ? "sm:border-t sm:border-gray-200" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                        {locationLabel}
                      </label>
                      <button
                        type="button"
                        onClick={() => removeAdditionalLocation(index)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        aria-label="Remove location"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                      <LocationFields
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        fieldPrefix={`additional_${index}_`}
                        address={location.address}
                        setAddress={(v) =>
                          updateAdditionalLocation(index, "address", v)
                        }
                        city={location.city}
                        setCity={(v) =>
                          updateAdditionalLocation(index, "city", v)
                        }
                        state={location.state}
                        setState={(v) =>
                          updateAdditionalLocation(index, "state", v)
                        }
                        country={location.country}
                        setCountry={(v) =>
                          updateAdditionalLocation(index, "country", v)
                        }
                        handleOnLoad={(instance) =>
                          handleAdditionalLocationOnLoad(instance, index)
                        }
                        handlePlaceChanged={() =>
                          handleAdditionalLocationPlaceChanged(index)
                        }
                        onChangeLocation={(vals) =>
                          setAdditionalLocations((prev) =>
                            prev.map((loc, i) =>
                              i === index
                                ? {
                                    ...loc,
                                    address: vals.address ?? loc.address,
                                    city: vals.city ?? loc.city,
                                    state: vals.state ?? loc.state,
                                    country: vals.country ?? loc.country,
                                  }
                                : loc,
                            ),
                          )
                        }
                        fullWidth
                      />
                    </div>
                  </div>
                );
              })}

              {/* Add Additional Location Button - Left margin */}
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                <div className="sm:col-span-1">
                  <button
                    type="button"
                    onClick={addAdditionalLocation}
                    className="text-indigo-600 text-sm cursor-pointer hover:underline hover:text-indigo-700 focus:outline-none"
                  >
                    Add Additional Location
                  </button>
                </div>
                <div className="sm:col-span-2"></div>
              </div>
            </div>
            {/* Preferred contact */}
            <div className="flex flex-col border-b border-gray-300 py-5 lg:py-6">
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 py-5 lg:py-6">
                <div className="flex items-center justify-between">
                  <h1 className="block text-sm font-medium text-gray-900 md:mt-0">
                    Preferred Contact *
                  </h1>
                </div>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                  <ContactFields
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    fieldPrefix="contact"
                    firstName={contactFirstName}
                    setFirstName={setContactFirstName}
                    lastName={contactLastName}
                    setLastName={setContactLastName}
                    title={contactTitle}
                    setTitle={setContactTitle}
                    email={contactEmail}
                    setEmail={setContactEmail}
                  />
                </div>
              </div>

              {/* Additional Contacts - reusing ContactFields per item */}
              {additionalContacts.map((contact, index) => {
                const contactLabel =
                  index === 0
                    ? "Additional Contact"
                    : `Additional Contact ${index + 1}`;
                return (
                  <div
                    key={index}
                    className={`sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 ${index === 0 ? "sm:border-t sm:border-gray-200" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                        {contactLabel}
                      </label>
                      <button
                        type="button"
                        onClick={() => removeAdditionalContact(index)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        aria-label="Remove contact"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                      <ContactFields
                        control={control}
                        errors={{}}
                        setValue={setValue}
                        fieldPrefix={`additional_${index}_`}
                        firstName={contact.firstName}
                        setFirstName={(v) =>
                          updateAdditionalContact(index, "firstName", v)
                        }
                        lastName={contact.lastName}
                        setLastName={(v) =>
                          updateAdditionalContact(index, "lastName", v)
                        }
                        title={contact.title}
                        setTitle={(v) =>
                          updateAdditionalContact(index, "title", v)
                        }
                        email={contact.email}
                        setEmail={(v) =>
                          updateAdditionalContact(index, "email", v)
                        }
                      />
                    </div>
                  </div>
                );
              })}

              {/* Add Additional Contact Button - Left margin */}
              <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:pt-6 sm:pb-2">
                <div className="sm:col-span-1">
                  <button
                    type="button"
                    onClick={addAdditionalContact}
                    className="text-indigo-600 text-sm cursor-pointer hover:underline hover:text-indigo-700 focus:outline-none"
                  >
                    Add Additional Contact
                  </button>
                </div>
                <div className="sm:col-span-2"></div>
              </div>
            </div>
            {/* RFI Section Checkbox */}
            {userHasRight(rights, "Request RFIs") && !isDuringMaintenance() && (
              <div className="sm:grid sm:grid-cols-1 sm:items-center sm:gap-4 sm:py-3 lg:py-3">
                <div className="mt-6 pb-5 space-y-3  px-0 md:px-1">
                  <div className="flex space-x-3">
                    <div>
                      {" "}
                      <input
                        type="checkbox"
                        id="showRfiSection"
                        checked={showRfiSection}
                        onChange={(e) => setShowRfiSection(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </div>
                    <div>
                      <h1 className="font-semibold">
                        Vendor Qualification Questionnaire (VQQ)
                      </h1>
                      <p className="text-sm text-gray-600">
                        Complete the information below to request a Vendor Qualification Questionnaire (VQQ)
                        document/(s) to be completed by a selected vendor.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* RFI Section - Only shown when checkbox is checked */}
            {showRfiSection && (
              <>
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6 ">
                  <h1 className="block ml-9 text-sm font-medium text-gray-900 mt-4 md:mt-0">
                    Questionnaire Templates*
                  </h1>
                  <div className="flex flex-col space-y-3">
                    <span className="space-x-3"></span>
                    <Controller
                      name="questionnaireTemplates"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Select Questionnaire Templates"
                          closeMenuOnSelect={false}
                          components={{
                            ...animatedComponents,
                            MultiValueRemove: (props) => {
                              // Disable remove button for Core RFI
                              if (
                                props.data.value ===
                                "106c4bdc-e1c2-4fca-9ab6-745b995c65ee"
                              ) {
                                return null; // Do not render the remove button
                              }
                              return <components.MultiValueRemove {...props} />;
                            },
                          }}
                          isMulti
                          options={questionnaireOptions?.map((option) => ({
                            ...option,
                          }))}
                          value={[
                            // Ensure Foundation - All is always selected
                            ...(field.value?.filter(
                              (item) =>
                                item.value !==
                                "106c4bdc-e1c2-4fca-9ab6-745b995c65ee",
                            ) || []),
                          ]}
                          onChange={(selected) => {
                            // Always include Foundation - All in the selected values
                            setValue("questionnaireTemplates", [
                              ...selected.filter(
                                (item) =>
                                  item.value !==
                                  "106c4bdc-e1c2-4fca-9ab6-745b995c65ee",
                              ),
                            ]);
                          }}
                          isClearable={field.value?.length > 1} // Allow clearing only if more than one option is selected
                          className="w-[330px] lg:w-[400px] xl:w-[500px] text-sm"
                          styles={customStyles} // Apply custom styles here
                        />
                      )}
                    />
                    {errors.questionnaireTemplates && (
                      <p className="text-red-500 text-sm">
                        {errors.questionnaireTemplates.message}
                      </p>
                    )}
                  </div>
                </div>{" "}
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6 border-b border-gray-300">
                  <h1 className="ml-9 block text-sm font-medium text-gray-900 mt-4 md:mt-0">
                    Location*
                  </h1>
                  <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
                    {availableLocations.length > 0 &&
                    availableLocations[0].address !== "" ? (
                      <>
                        {availableLocations.length === 1 ? (
                          // Single location - show as plain text like preferred contact
                          <div className="sm:max-w-xl text-sm sm:pt-1.5">
                            <span className="text-gray-900">
                              <span className="font-medium">
                                {availableLocations[0].label}:
                              </span>{" "}
                              {availableLocations[0].fullAddress}
                            </span>
                          </div>
                        ) : (
                          // Multiple locations - show custom dropdown with bold labels
                          <div className="sm:max-w-xl relative rfi-location-dropdown">
                            <div
                              className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors.rfiLocation ? "ring-red-300" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors.rfiLocation ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setIsRFILocationDropdownOpen(
                                    !isRFILocationDropdownOpen,
                                  )
                                }
                                onBlur={(e) =>
                                  onBlurSelectWithDelay(
                                    e,
                                    setIsRFILocationDropdownOpen,
                                  )
                                } // Use the new onBlurSelect function
                                className="relative w-full cursor-pointer bg-transparent py-1.5 pl-3 pr-10 text-left border-0 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                              >
                                <span className="flex items-center truncate text-gray-900">
                                  {currentSelectedRFILocation ? (
                                    (() => {
                                      const selected = availableLocations.find(
                                        (loc) =>
                                          loc.id ===
                                          currentSelectedRFILocation.id,
                                      );
                                      return selected ? (
                                        selected.isNewLocation ? (
                                          // Special styling for "New Location" option
                                          <span className="italic text-gray-500">
                                            {selected.fullAddress}
                                          </span>
                                        ) : (
                                          // Regular location display
                                          <>
                                            <span className="font-medium">
                                              {selected.label}:
                                            </span>{" "}
                                            <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={selected.fullAddress}>
                                              {selected.fullAddress}
                                            </CustomTooltip>
                                          </>
                                        )
                                      ) : (
                                        "Select VQQ location"
                                      );
                                    })()
                                  ) : (
                                    <span className="text-gray-500">
                                      Select VQQ location
                                    </span>
                                  )}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                  <ChevronDownIcon
                                    className="h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                </span>
                              </button>
                            </div>

                            {isRFILocationDropdownOpen && (
                              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                {availableLocations.map((location) => (
                                  <div
                                    key={location.id}
                                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                                      location.isNewLocation
                                        ? "border-t border-gray-200 mt-1 pt-3" // Add separator before "New Location"
                                        : ""
                                    } ${
                                      currentSelectedRFILocation.id ===
                                      location.id
                                        ? "text-indigo-600 bg-indigo-50"
                                        : "text-gray-900 hover:bg-gray-50"
                                    }`}
                                    onClick={() => {
                                      currentSetSelectedRFILocation(location);
                                      setIsRFILocationDropdownOpen(false);
                                    }}
                                  >
                                    <span className="flex items-center truncate">
                                      {location.isNewLocation ? (
                                        // Special styling for "New Location"
                                        <span className="italic text-gray-500">
                                          {location.fullAddress}
                                        </span>
                                      ) : (
                                        // Regular location display
                                        <>
                                          <span className="font-medium">
                                            {location.label}:
                                          </span>{" "}
                                          <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={location.fullAddress}>
                                            {location.fullAddress}
                                          </CustomTooltip>
                                        </>
                                      )}
                                    </span>
                                    {currentSelectedRFILocation.id ===
                                      location.id && (
                                      <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                        <CheckIcon
                                          className="h-5 w-5 text-indigo-600"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // No locations available - show message like preferred contact
                      <div className="sm:max-w-xl text-sm sm:pt-1.5">
                        <span className="text-gray-500 italic">
                          Please first select or add a vendor in the section
                          above.
                        </span>
                      </div>
                    )}
                    {errors.rfiLocation && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.rfiLocation}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6 border-b border-gray-300">
                  <h1 className="ml-9 block text-sm font-medium text-gray-900 mt-4 md:mt-0">
                    Contact
                  </h1>
                  <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
                    {availableContacts.length > 0 &&
                    availableContacts[0].id !== "" ? (
                      <>
                        {availableContacts.length === 1 ? (
                          // Single location - show as plain text like preferred contact
                          <div className="sm:max-w-xl text-sm sm:pt-1.5">
                            <span className="text-gray-900">
                              <span className="font-medium">
                                {availableContacts[0].label}:
                              </span>{" "}
                              {availableContacts[0].fullContact}
                            </span>
                          </div>
                        ) : (
                          // Multiple contacts - show custom dropdown with bold labels
                          <div className="sm:max-w-xl relative rfi-location-dropdown">
                            <div
                              className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors.rfiContact ? "ring-red-300" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors.rfiContact ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setIsRFIContactDropdownOpen(
                                    !isRFIContactDropdownOpen,
                                  )
                                }
                                onBlur={(e) =>
                                  onBlurSelectWithDelay(
                                    e,
                                    setIsRFIContactDropdownOpen,
                                  )
                                } // Use the new onBlurSelect function
                                className="relative w-full cursor-pointer bg-transparent py-1.5 pl-3 pr-10 text-left border-0 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                              >
                                <span className="block truncate text-gray-900">
                                  {currentSelectedRFIContact ? (
                                    (() => {
                                      const selected = availableContacts.find(
                                        (loc) =>
                                          loc.id ===
                                          currentSelectedRFIContact.id,
                                      );
                                      return selected ? (
                                        selected.isNewLocation ? (
                                          <span className="italic text-gray-500">
                                            {selected.fullContact}
                                          </span>
                                        ) : (
                                          // Regular contact display
                                          <>
                                            <span className="font-medium">
                                              {selected.label}:
                                            </span>{" "}
                                            {selected.fullContact}
                                          </>
                                        )
                                      ) : (
                                        "Select VQQ contact"
                                      );
                                    })()
                                  ) : (
                                    <span className="text-gray-500">
                                      Select VQQ contact
                                    </span>
                                  )}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                  <ChevronDownIcon
                                    className="h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                </span>
                              </button>
                            </div>

                            {isRFIContactDropdownOpen && (
                              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                {availableContacts.map((contact) => (
                                  <div
                                    key={contact.id}
                                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                                      contact.isNewLocation
                                        ? "border-t border-gray-200 mt-1 pt-3" // Add separator before "New Location"
                                        : ""
                                    } ${
                                      currentSelectedRFIContact.id ===
                                      contact.id
                                        ? "text-indigo-600 bg-indigo-50"
                                        : "text-gray-900 hover:bg-gray-50"
                                    }`}
                                    onClick={() => {
                                      currentSetSelectedRFIContact(contact);
                                      setIsRFIContactDropdownOpen(false);
                                    }}
                                  >
                                    <span className="block truncate">
                                      {contact.isNewLocation ? (
                                        // Special styling for "New Location"
                                        <span className="italic text-gray-500">
                                          {contact.fullContact}
                                        </span>
                                      ) : (
                                        // Regular location display
                                        <>
                                          <span className="font-medium">
                                            {contact.label}:
                                          </span>{" "}
                                          {contact.fullContact}
                                        </>
                                      )}
                                    </span>
                                    {currentSelectedRFIContact.id ===
                                      contact.id && (
                                      <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                        <CheckIcon
                                          className="h-5 w-5 text-indigo-600"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // No contacts available - show message like preferred contact
                      <div className="sm:max-w-xl text-sm sm:pt-1.5">
                        <span className="text-gray-500 italic">
                          Please first select or add a vendor in the section
                          above.
                        </span>
                      </div>
                    )}
                    {errors.rfiContact && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.rfiContact}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6 ">
                  <h1 className="block ml-9 text-sm font-medium text-gray-900 mt-4 md:mt-0">
                    Expected Due Date*
                  </h1>
                  <div className="flex flex-col space-y-3">
                    <span className="space-x-3">
                      <div className="w-[330px] lg:w-[400px] xl:w-[500px]">
                        <DatePickerController
                          control={control}
                          fieldName={"expectedDate"}
                          className="w-full"
                        />
                      </div>
                    </span>
                    {errors.expectedDate?.startDate && (
                      <p className="text-red-500 text-sm">
                        {errors.expectedDate.startDate.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6 ">
                  <h1 className="block ml-9 text-sm font-medium text-gray-900">
                    VQQ Additional Details
                  </h1>
                  <div>
                    <textarea
                      {...register("rfiAdditionalDetails")}
                      placeholder="Provide additional request details"
                      className="border-gray-300 rounded-md text-sm w-[330px] lg:w-[400px] xl:w-[500px]"
                    />
                    {errors.rfiAdditionalDetails && (
                      <p className="text-red-500 text-sm">
                        {errors.rfiAdditionalDetails.message}
                      </p>
                    )}
                  </div>
                </div>
                {riskAddOns.showAddOns && (
                  <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 py-5 lg:py-6">
                    <h1 className="block ml-9 text-sm font-medium text-gray-900">
                      Add-Ons
                    </h1>
                    <div className="w-[330px] lg:w-[400px] xl:w-[500px]">
                      <fieldset className="rounded-md border border-gray-300 p-4">
                        <legend className="px-1 text-sm font-medium text-gray-900">
                          Risk Scoring &amp; Gap Analysis
                        </legend>
                        <div className="space-y-3">
                          {canRequestAutoScore && (
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={enableAutoScore}
                                onChange={(e) => setEnableAutoScore(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                              />
                              <span className="text-sm text-gray-900">
                                <span className="font-medium">
                                  Diligent Risk Model Auto-Score
                                </span>
                                <span className="mt-0.5 block text-xs text-gray-500">
                                  When the vendor submits, each response is auto-scored against the Diligent Risk Model and a Diligent admin reviews the findings before publishing. No gap-analysis comments required.
                                </span>
                              </span>
                            </label>
                          )}
                          {canRequestSmeScore && (
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={enableSmeScore}
                                onChange={(e) => setEnableSmeScore(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                              />
                              <span className="text-sm text-gray-900">
                                <span className="font-medium">
                                  Diligent SME-Score and Gap Analysis
                                </span>
                                <span className="mt-0.5 block text-xs text-gray-500">
                                  A Diligent subject-matter expert reviews every non-Compliant finding, adjusts severity as needed, and writes a gap-analysis comment for each one before publishing. Best for VQQs you intend to act on contractually.
                                </span>
                              </span>
                            </label>
                          )}
                        </div>
                      </fieldset>
                    </div>
                  </div>
                )}
              </>
            )}
            {/* Assessment Section Checkbox */}
            {userHasRight(rights, "Request Audit Reports") && (
              <div className="sm:grid sm:grid-cols-1 sm:items-center sm:gap-4 sm:py-3 lg:py-3 border-t border-gray-300">
                <div className="mt-6 pb-5 space-y-3 px-0 md:px-1">
                  <div className="flex space-x-3">
                    <div>
                      <input
                        type="checkbox"
                        id="showAssessmentSection"
                        checked={showAssessmentSection}
                        onChange={(e) =>
                          setShowAssessmentSection(e.target.checked)
                        }
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </div>
                    <div>
                      <h1 className="font-semibold">Audit Request</h1>
                      <p className="text-sm text-gray-600">
                        Complete the information below to request a formal
                        audited assessment report.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Assessment Section - Only shown when checkbox is checked */}
            {showAssessmentSection && (
              <>
                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                  <h1 className="ml-9 block text-sm font-medium text-gray-900 mt-4 md:mt-0">
                    Assessment Location*
                  </h1>
                  <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
                    {availableLocations.length > 0 &&
                    availableLocations[0].address !== "" ? (
                      <>
                        {availableLocations.length === 1 ? (
                          // Single location - show as plain text like preferred contact
                          <div className="sm:max-w-xl text-sm sm:pt-1.5">
                            <span className="text-gray-900">
                              <span className="font-medium">
                                {availableLocations[0].label}:
                              </span>{" "}
                              {availableLocations[0].fullAddress}
                            </span>
                          </div>
                        ) : (
                          // Multiple locations - show custom dropdown with bold labels
                          <div className="sm:max-w-xl relative rfi-location-dropdown">
                            <div
                              className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors.assessmentLocation ? "ring-red-300" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors.assessmentLocation ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setIsAssessmentLocationDropdownOpen(
                                    !isAssessmentLocationDropdownOpen,
                                  )
                                }
                                onBlur={(e) =>
                                  onBlurSelectWithDelay(
                                    e,
                                    setIsAssessmentLocationDropdownOpen,
                                  )
                                } // Use the new onBlurSelect function
                                className="relative w-full cursor-pointer bg-transparent py-1.5 pl-3 pr-10 text-left border-0 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                              >
                                <span className="flex- items-center truncate text-gray-900">
                                  {currentSelectedAssessmentLocation ? (
                                    (() => {
                                      const selected = availableLocations.find(
                                        (loc) =>
                                          loc.id ===
                                          currentSelectedAssessmentLocation.id,
                                      );
                                      return selected ? (
                                        selected.isNewLocation ? (
                                          // Special styling for "New Location" option
                                          <span className="italic text-gray-500">
                                            {selected.fullAddress}
                                          </span>
                                        ) : (
                                          // Regular location display
                                          <>
                                            <span className="font-medium">
                                              {selected.label}:
                                            </span>{" "}
                                            <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={selected.fullAddress}>
                                              {selected.fullAddress}
                                            </CustomTooltip>
                                          </>
                                        )
                                      ) : (
                                        "Select Assessment location"
                                      );
                                    })()
                                  ) : (
                                    <span className="text-gray-500">
                                      Select Assessment location
                                    </span>
                                  )}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                  <ChevronDownIcon
                                    className="h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                </span>
                              </button>
                            </div>

                            {isAssessmentLocationDropdownOpen && (
                              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                {availableLocations.map((location) => (
                                  <div
                                    key={location.id}
                                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                                      location.isNewLocation
                                        ? "border-t border-gray-200 mt-1 pt-3" // Add separator before "New Location"
                                        : ""
                                    } ${
                                      currentSelectedAssessmentLocation.id ===
                                      location.id
                                        ? "text-indigo-600 bg-indigo-50"
                                        : "text-gray-900 hover:bg-gray-50"
                                    }`}
                                    onClick={() => {
                                      currentSetSelectedAssessmentLocation(
                                        location,
                                      );
                                      setIsAssessmentLocationDropdownOpen(
                                        false,
                                      );
                                    }}
                                  >
                                    <span className="flex items-center truncate">
                                      {location.isNewLocation ? (
                                        // Special styling for "New Location"
                                        <span className="italic text-gray-500">
                                          {location.fullAddress}
                                        </span>
                                      ) : (
                                        // Regular location display
                                        <>
                                          <span className="font-medium">
                                            {location.label}:
                                          </span>{" "}
                                          <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={location.fullAddress}>
                                            {location.fullAddress}
                                          </CustomTooltip>
                                        </>
                                      )}
                                    </span>
                                    {currentSelectedAssessmentLocation.id ===
                                      location.id && (
                                      <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                        <CheckIcon
                                          className="h-5 w-5 text-indigo-600"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // No locations available - show message like preferred contact
                      <div className="sm:max-w-xl text-sm sm:pt-1.5">
                        <span className="text-gray-500 italic">
                          Please first select or add a vendor in the section
                          above.
                        </span>
                      </div>
                    )}
                    {errors.assessmentLocation && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.assessmentLocation}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6 border-b border-gray-300">
                  <h1 className="ml-9 block text-sm font-medium text-gray-900 mt-4 md:mt-0">
                    Contact
                  </h1>
                  <div className="mt-2 sm:col-span-2 sm:mt-0 w-[330px] lg:w-[400px] xl:w-[500px]">
                    {availableContacts.length > 0 &&
                    availableContacts[0].id !== "" ? (
                      <>
                        {availableContacts.length === 1 ? (
                          // Single location - show as plain text like preferred contact
                          <div className="sm:max-w-xl text-sm sm:pt-1.5">
                            <span className="text-gray-900">
                              <span className="font-medium">
                                {availableContacts[0].label}:
                              </span>{" "}
                              {availableContacts[0].fullContact}
                            </span>
                          </div>
                        ) : (
                          // Multiple contacts - show custom dropdown with bold labels
                          <div className="sm:max-w-xl relative rfi-location-dropdown">
                            <div
                              className={`flex rounded-md shadow-sm ring-1 ring-inset ${errors.assessmentContact ? "ring-red-300" : "ring-gray-200"} focus-within:ring-2 focus-within:ring-inset ${errors.assessmentContact ? "focus-within:ring-red-500" : "focus-within:ring-indigo-600"} hover:bg-gray-50 transition-colors`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setIsAssessmentContactDropdownOpen(
                                    !isAssessmentContactDropdownOpen,
                                  )
                                }
                                onBlur={(e) =>
                                  onBlurSelectWithDelay(
                                    e,
                                    setIsAssessmentContactDropdownOpen,
                                  )
                                } // Use the new onBlurSelect function
                                className="relative w-full cursor-pointer bg-transparent py-1.5 pl-3 pr-10 text-left border-0 focus:ring-0 focus:outline-none sm:text-sm sm:leading-6"
                              >
                                <span className="block truncate text-gray-900">
                                  {currentSelectedAssessmentContact ? (
                                    (() => {
                                      const selected = availableContacts.find(
                                        (loc) =>
                                          loc.id ===
                                          currentSelectedAssessmentContact.id,
                                      );
                                      return selected ? (
                                        selected.isNewLocation ? (
                                          <span className="italic text-gray-500">
                                            {selected.fullContact}
                                          </span>
                                        ) : (
                                          // Regular contact display
                                          <>
                                            <span className="font-medium">
                                              {selected.label}:
                                            </span>{" "}
                                            {selected.fullContact}
                                          </>
                                        )
                                      ) : (
                                        "Select VQQ contact"
                                      );
                                    })()
                                  ) : (
                                    <span className="text-gray-500">
                                      Select VQQ contact
                                    </span>
                                  )}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                  <ChevronDownIcon
                                    className="h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                </span>
                              </button>
                            </div>

                            {isAssessmentContactDropdownOpen && (
                              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                {availableContacts.map((contact) => (
                                  <div
                                    key={contact.id}
                                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                                      contact.isNewLocation
                                        ? "border-t border-gray-200 mt-1 pt-3" // Add separator before "New Location"
                                        : ""
                                    } ${
                                      currentSelectedAssessmentContact.id ===
                                      contact.id
                                        ? "text-indigo-600 bg-indigo-50"
                                        : "text-gray-900 hover:bg-gray-50"
                                    }`}
                                    onClick={() => {
                                      currentSetSelectedAssessmentContact(
                                        contact,
                                      );
                                      setIsAssessmentContactDropdownOpen(false);
                                    }}
                                  >
                                    <span className="block truncate">
                                      {contact.isNewLocation ? (
                                        // Special styling for "New Location"
                                        <span className="italic text-gray-500">
                                          {contact.fullContact}
                                        </span>
                                      ) : (
                                        // Regular location display
                                        <>
                                          <span className="font-medium">
                                            {contact.label}:
                                          </span>{" "}
                                          {contact.fullContact}
                                        </>
                                      )}
                                    </span>
                                    {currentSelectedAssessmentContact.id ===
                                      contact.id && (
                                      <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                        <CheckIcon
                                          className="h-5 w-5 text-indigo-600"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // No contacts available - show message like preferred contact
                      <div className="sm:max-w-xl text-sm sm:pt-1.5">
                        <span className="text-gray-500 italic">
                          Please first select or add a vendor in the section
                          above.
                        </span>
                      </div>
                    )}
                    {errors.assessmentContact && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.assessmentContact}
                      </p>
                    )}
                  </div>
                </div>
                {/* Service Categories */}
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
                  <h1 className="block ml-9 text-sm font-medium text-gray-900">
                    Service Categories*
                  </h1>
                  <div>
                    <Controller
                      name="serviceCategories"
                      control={control}
                      render={({ field }) => {
                        const rawValue = field.value ?? [];
                        const seen = new Set();
                        const value = rawValue.filter((opt) => {
                          const k = `${String(opt?.value)}::${String(opt?.label ?? "")
                            .trim()
                            .toLowerCase()}`;
                          if (seen.has(k)) return false;
                          seen.add(k);
                          return true;
                        });
                        return (
                        <Select
                          {...field}
                          closeMenuOnSelect={false}
                          components={animatedComponents}
                          isMulti
                          options={serviceCategoryOptions}
                          getOptionValue={(o) => String(o.value)}
                          getOptionLabel={(o) => o.label}
                          value={value}
                          onChange={(selected) => {
                            setValue("serviceCategories", selected ?? [], {
                              shouldValidate: true,
                            });
                          }}
                          isClearable={value.length > 0}
                          className="w-[330px] lg:w-[400px] xl:w-[500px]"
                          styles={customStyles}
                        />
                        );
                      }}
                    />

                    {errors.serviceCategories && (
                      <p className="text-red-500 text-sm">
                        {errors.serviceCategories.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
                  <h1 className="block ml-9 text-sm font-medium text-gray-900">
                    Onsite / Remote*
                  </h1>
                  <div className="md:w-[390px]">
                    {/* Controller for Onsite/Remote Dropdown */}
                    <Controller
                      name="onsiteRemote" // Matches the Yup schema
                      control={control}
                      defaultValue={{ onsite: false, remote: true }} // Default value is Remote
                      render={({ field }) => (
                        <ComboBox
                          data={[
                            { value: false, name: "Remote" },
                            { value: true, name: "Onsite" },
                          ]}
                          placeHolder="Select..."
                          setSelected={(value) => {
                            // Update the value of onsiteRemote dynamically
                            setValue("onsiteRemote", {
                              onsite: value.name === "Onsite",
                              remote: value.name === "Remote",
                            });
                          }}
                          selected={
                            field.value.onsite
                              ? { value: true, name: "Onsite" }
                              : { value: false, name: "Remote" }
                          }
                          width="w-[500px]"
                          inputWidth="w-[500px]"
                        />
                      )}
                    />

                    {/* Display validation errors */}
                    {errors.onsiteRemote && (
                      <p className="text-red-500 text-sm">
                        {errors.onsiteRemote.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Desired Initial Draft Date */}
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
                  <h1 className="block ml-9 text-sm font-medium text-gray-900">
                    Desired Initial Draft Date*
                  </h1>
                  <div className="w-[300px] lg:w-[400px] xl:w-[500px]">
                    <Controller
                      name="draftDate"
                      control={control}
                      render={({ field }) => (
                        <div className="relative w-full">
                          <Datepicker
                            {...field}
                            name="draftDate"
                            onChange={field.onChange}
                            placeholder="Select Date"
                            useRange={false}
                            displayFormat="MMMM DD, YYYY"
                            asSingle
                            startFrom={fortyFiveDays}
                            minDate={fortyFiveDays} // Restrict to current or upcoming dates
                            containerClassName="datepicker-dropdown w-[330px] lg:w-[400px] xl:w-[500px]"
                            popperClassName="datepicker-dropdown w-[330px] lg:w-[400px] xl:w-[500px] p-2 border border-gray-300 rounded-lg shadow-lg bg-white grid grid-cols-2 gap-1 max-h-[200px] overflow-y-auto"
                            inputClassName="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                          />
                        </div>
                      )}
                    />
                    {errors.draftDate && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.draftDate.startDate.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional Details */}
                <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 mt-4 md:mt-0">
                  <h1 className="block ml-9 text-sm font-medium text-gray-900">
                    Additional Details
                  </h1>
                  <div>
                    <textarea
                      {...register("additionalDetails")}
                      placeholder="Provide additional request details"
                      className="border-gray-300 rounded-md text-sm  w-[330px] lg:w-[400px] xl:w-[500px] "
                    />{" "}
                    {errors.additionalDetails && (
                      <p className="text-red-500 text-sm">
                        {errors.additionalDetails.message}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </fieldset>
          {/* Submit button outside fieldset so it can show loading state */}
          <div className="mt-10 flex items-center justify-end gap-x-6 border-t py-2 px-5">
            <button
              type="button"
              disabled={formLoading}
              onClick={(e) => {
                e.preventDefault();
                submitWithLocationValidation(onSubmit)();
              }}
              className="inline-flex justify-center items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {formLoading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </FormProvider>
      <ProviderContactDrawer
        open={contactOpen}
        setOpen={setContactOpen}
        register={register}
        setFormData={setFormData}
        formData={formData}
        watch={watch}
        onAddContact={handleAddContact}
      />{" "}
      <NotificationModal
        setOpen={setShow}
        open={show}
        title={
          isMutationError
            ? "Submission failed"
            : notifyAsError
              ? "Cannot submit vendor request"
              : getNotificationTitle()
        }
        desc={
          isVqaError
            ? vqaError?.response?.data?.message ||
              "Failed to submit vendor request. Please try again."
            : isRfiError
              ? rfiError?.response?.data?.message ||
                "Failed to submit vendor request. Please try again."
              : message ||
                "Your vendor request has been submitted and is now being processed. You can track vendors on your vendor discovery page."
        }
        error={modalShowsError}
        primaryButtonText={!modalShowsError ? "View Vendors" : undefined}
        onPrimaryClick={
          !modalShowsError
            ? () => {
                setShow(false);
                navigate("/Providers");
              }
            : undefined
        }
        onClose={handleNotificationModalClose}
      />{" "}
    </div>
  );
};
